import useLiquidityPools from '@/application/read/pools/useLiquidityPools';
import useVault from '@/application/read/vaults/useVault';
import { useModalContext } from '@/presentation/context/modal';
import { Asset, Zero } from 'nftx.js';
import { useFormatter, useTranslations } from 'use-intl';
import VaultIcon from '../../elements/VaultIcon';
import Price from '../../elements/Price';
import { formatEther } from 'viem';
import ChoiceButton from '../../elements/ChoiceButton';
import Link from 'next/link';
import { useNetwork } from '@/presentation/context/network';
import { getCollectionPath } from '@/presentation/utils/paths';

const EligibleVault = ({
  vaultId,
  collectionAssets,
}: {
  vaultId: string;
  collectionAssets?: Asset[];
}) => {
  const t = useTranslations('Profile.Balances.EligibleVaults');
  const { number } = useFormatter();
  const { slug } = useNetwork();
  const { data: vault } = useVault({ vaultId });
  const { data: pools } = useLiquidityPools({ vaultId, exists: true });
  const { showSwapModal, showCreatePositionModal } = useModalContext();

  const bestApr = (pools ?? []).reduce((max, pool) => {
    if (pool.apr['1m'] > max) {
      return pool.apr['1m'];
    }
    return max;
  }, Zero);
  const sellPrice = vault?.prices[0].mint.price ?? Zero;

  const volume = pools?.reduce((acc, cur) => {
    return acc + cur.weeklyVolume;
  }, Zero);

  if (!vault) return null;

  return (
    <tr key={vaultId}>
      <td className="p-2 text-left">
        <Link
          className="flex items-center"
          href={getCollectionPath({
            chain: slug,
            collectionSlug: vault.collectionSlug,
            tab: 'your-items',
            vaultSlug: vault?.slug,
          })}
        >
          <VaultIcon vaultId={vaultId} className="mr-4 w-8" />{' '}
          {vault?.token.name}
        </Link>
      </td>
      <td className="text-right p-2">
        {collectionAssets?.filter((a) => a.vaultIds.includes(vaultId)).length}
      </td>
      <td className="text-right p-2">
        {sellPrice > 0 ? <Price value={sellPrice} /> : '-'}
      </td>
      <td className="text-right p-2">
        {bestApr > 0
          ? number(Number(formatEther(bestApr)), { style: 'percent' })
          : '-'}
      </td>
      <td className="text-right p-2">{<Price value={volume} />}</td>
      <td className="text-right p-2">
        <ChoiceButton
          options={[
            {
              label: t('deposit'),
              onClick: () =>
                showCreatePositionModal({
                  vaultId,
                  using: 'nft',
                }),
            },
            {
              label: t('sell'),
              onClick: () => {
                showSwapModal({
                  sellVaultId: vaultId,
                  sellType: 'NFT',
                  sellToken: vault?.asset.id,
                  sellVaultAddress: vault?.id,
                  buyType: 'ETH',
                  searchView: 'wallet',
                });
              },
            },
            {
              label: t('swap'),
              onClick: () =>
                showSwapModal({
                  sellVaultId: vaultId,
                  sellType: 'NFT',
                  sellToken: vault?.asset.id,
                  sellVaultAddress: vault?.id,
                  buyType: 'NFT',
                  buyVaultId: vaultId,
                  buyToken: vault?.asset.id,
                  buyVaultAddress: vault?.id,
                  searchView: 'wallet',
                }),
            },
          ]}
        >
          {t('deposit')}
        </ChoiceButton>
      </td>
    </tr>
  );
};

export default EligibleVault;
